import { Box, Grid, Stack, Step, StepButton, Stepper } from '@mui/material';
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { MprcContainer, MprcMessageBox, MprcRequestErrorDialog, MprcRequestIndicator } from 'components';
import { ButtonBack } from 'components/buttons';
import { ButtonSave } from 'components/buttons/ButtonSave';
import { ManagedEnrollmentTrainingsListOrderBy, ManageEnrollOperation } from 'generated/CoreApi_v1';
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { EditEnrollmentStepProps, EnrollmentEditActions } from 'views/lms/enrollment/Home';
import { useEnrollmentManagementContext } from 'views/lms/enrollment/Home/EnrollmentManagementWrapper';
import { EnrollmentManagerHeader } from 'views/lms/enrollment/Home/EnrollmentManagerHeader';

////////////////////////////////////////////////////////////////////////////

interface IStep {
    name: string;
    enabled: boolean;
    visible: boolean;
    render: React.FC<EditEnrollmentStepProps>;
}

interface IEditStepsProps {
    steps: IStep[];
}

export const StepManagement: React.FC<IEditStepsProps> = (props) => {

    const { trainingId, operationId } = useParams();
    const [searchParams] = useSearchParams();
    const classroomId = searchParams.has("c") ? searchParams.get("c") : null;

    const { t } = useTranslation("lms");
    const api = useCoreApi();
    const emc = useEnrollmentManagementContext();
    const navigate = useNavigate();
    const training = emc.training;

    const [currentStep, setCurrentStep] = useState(0);
    const [canSave, setCanSave] = useState<boolean>(false);
    const [action, setAction] = useState<EnrollmentEditActions>("none");
    const [loading, setLoading] = useState<boolean>(false);
    const [trainingNotFound, setTrainingNotFound] = useState<boolean>(false);

    const [viewError, setViewError] = useState<any>(null);
    const [steps, setSteps] = useState<IStep[]>(props.steps);

    const numberOfSteps = useRef<number>(4);
    const maxStep = useRef<number>(0);

    ////////////////////////////////////////////////////////////////////////////

    ///
    const goBack = () => {
        if (trainingId && currentStep == 0) {
            navigate(-1);
        } else {
            setCurrentStep(state => state > 0 ? state - 1 : state);
        }
    };

    ///
    const getHeaderItems = () => {
        const actions: React.ReactNode[] = [];
        const rightOptions: React.ReactNode[] = [];
        const canBack = (currentStep > 0 && currentStep < 3) || trainingId;

        if (canBack) actions.push(
            <ButtonBack
                key="btnBack"
                rounded
                variant="outlined"
                onClick={goBack}
            />
        );

        if ((trainingId == undefined && currentStep == 1) || (trainingId && currentStep == 0)) {
            rightOptions.push(<ButtonSave
                text={t("Enrollment.Common.ConfirmButtonText")}
                title={t("Enrollment.Common.ConfirmButtonTitle")}
                key="btnSave" rounded variant="text"
                disabled={!canSave || action == "confirm"}
                onClick={() => setAction("confirm")}
            />);
        }

        actions.push([<Stack key="editContentHeaderRightOptions" direction="row" gap={1} alignItems="center">{rightOptions}</Stack>]);

        return actions;
    };

    //
    async function selectTraining() {
        setLoading(true);

        try {
            if (emc.unitTypes == undefined) {
                let result = await api.v1.unitType.getUnitTypes();
                emc.setUnitTypes(result.data);
            }

            if (emc.userSettings == undefined) {
                let result = await api.v1.foundationSettings.getUserSettings();
                emc.setUserSettings(result.data);
            }

            const result = await api.v1.enrollmentOperation.getEnrollmentTrainings({
                searchText: null,
                trainingId: trainingId!,
                classroomId: classroomId,
                filter: [],
                orderBy: ManagedEnrollmentTrainingsListOrderBy.CreatedOn,
                pageNumber: 1,
                addCompletedClassroom: true,
                pageSize: 1
            });

            if (result.data.totalRows == 0) {
                setTrainingNotFound(true);
            } else {
                setSteps(steps.filter(x => x.name != t("Enrollment.StepTraining.TabName")));

                var item = result.data.items[0];
                emc.setTraining({ ...item, totalSelected: 0 });
                emc.setOperation(ManageEnrollOperation[(operationId as any) as keyof typeof ManageEnrollOperation]);

                if (item.classCount == 1) {
                    emc.setClassroom({
                        classroomId: item.classroomId,
                        name: item.multipleClasses ? item.classroomName : item.name,
                        timezoneId: item.timezoneId,
                        enrollSuspended: item.enrollSuspended,
                        availableEnrollmentsCount: item.availableEnrollmentsCount,
                        executionStartDate: item.executionStartDate,
                        executionDueDateOption: item.executionDueDateOption,
                        executionDueDate: item.executionDueDate,
                        executionDueDays: item.executionDueDays
                    });
                } else {
                    emc.setClassroom(undefined);
                }
            }
        }
        catch (e) {
            setViewError(e);
        }
        finally {
            setLoading(false);
        }
    }

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT's
    ////////////////////////////////////////////////////////////////////////////

    ///
    useEffect(() => {
        numberOfSteps.current = 4;
        maxStep.current = numberOfSteps.current - 1;
        setAction("none");

        setSteps((state) =>
            state.map(
                (s, index) => (
                    {
                        name: s.name,
                        enabled: index <= currentStep && currentStep < 3,
                        visible: index < 4,
                        render: s.render
                    }
                )
            )
        );

        if (currentStep != (trainingId ? 0 : 1)) setCanSave(false);

    }, [training, currentStep]);

    ///
    useEffect(() => {
        if (trainingId) selectTraining();
    }, [document.location.pathname, trainingId]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    const events: EditEnrollmentStepProps = {
        canSave, setCanSave,
        goNextStep: () => setCurrentStep(state => state < 4 ? state + 1 : state),
        goFirstStep: () => setCurrentStep(0),
        action
    };

    const RenderStep = steps[currentStep].render;

    return <>
        <Box height="100%">

            <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(null)} />

            {
                loading
                    ?
                    <MprcRequestIndicator visible={loading} type="request" />
                    : trainingNotFound
                        ?
                        <MprcMessageBox
                            variant="error"
                            message={t("Enrollment.Common.TrainingNotFoundMessage")}
                        />
                        :
                        <Grid container direction="column" height="100%">
                            <Grid width="100%">
                                {
                                    currentStep > (trainingId ? -1 : 0) &&
                                    <EnrollmentManagerHeader
                                        recordChanged={true}
                                        onClose={goBack}
                                        actions={getHeaderItems()}
                                    />
                                }
                                <section id="steps">
                                    <MprcContainer>
                                        <Stepper
                                            activeStep={currentStep}
                                            orientation="horizontal"
                                            nonLinear
                                        >
                                            {
                                                steps
                                                    .filter(p => p.visible)
                                                    .map((s, index) =>
                                                        <Step
                                                            key={index}
                                                            disabled={!steps[index].enabled}
                                                            onClick={
                                                                () => steps[index].enabled && setCurrentStep(index)
                                                            }
                                                        >
                                                            <StepButton
                                                                sx={(theme) => ({
                                                                    "& .MuiStepLabel-labelContainer": {
                                                                        color: steps[index].enabled ? theme.palette.common.black : undefined,
                                                                    },
                                                                    "& svg": {
                                                                        color: index < currentStep ? theme.palette.info.main : undefined,
                                                                    }
                                                                })}
                                                                className="Mui-active"
                                                            >
                                                                {s.name}
                                                            </StepButton>
                                                        </Step>
                                                    )
                                            }
                                        </Stepper>
                                    </MprcContainer>
                                </section>
                            </Grid>

                            <Grid size="grow" overflow="auto" className={AppGlobalClassKeys.thinScrollbar} width="100%">
                                <Grid container direction="column" height="100%" width="100%">
                                    <Grid size="grow" width="100%">
                                        <RenderStep {...events} />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
            }
        </Box>
    </>;
};
